import { SimpleGrid } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { normalisePostcodeForDisplay } from '@duke-hq/google-address/services/transform/postcode';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { SectionCard } from '../components';
import { useCardEdit } from '../useCardEdit';
import { AddressEditForm } from './AddressEditForm';
import { testIds } from '../../testIds';
import type { EmployerDetail } from '../types';

export function AddressCard({ employer }: { employer: EmployerDetail }): React.ReactElement {
  const { isOpen, onOpen, onClose, onSuccess, onError } = useCardEdit();
  const update = trpcAdmin.workPlacements.employers.updateEmployerAddress.useMutation({
    onSuccess,
    onError,
  });

  const street =
    [employer.addressStreetNumber, employer.addressStreetName].filter(Boolean).join(' ') || null;

  return (
    <SectionCard
      title={<Trans>Address</Trans>}
      onEdit={onOpen}
      data-testid={testIds.drawerAddressSection}
    >
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
        <SingleField name={<Trans>Street</Trans>} value={street} />
        <SingleField name={<Trans>City/Town/Suburb</Trans>} value={employer.addressLocality} />
        <SingleField name={<Trans>Unit/Suite/Building</Trans>} value={employer.addressSubpremise} />
        <SingleField name={<Trans>State</Trans>} value={employer.addressState} />
        <SingleField
          name={<Trans>Postcode</Trans>}
          value={normalisePostcodeForDisplay(employer.addressPostcode)}
        />
      </SimpleGrid>

      <FormModalWrapper
        isOpen={isOpen}
        onClose={onClose}
        modalContentHeight="auto"
        modalContent={
          <AddressEditForm
            employer={employer}
            onSave={(payload) => update.mutate({ employerId: employer.id, ...payload })}
            onCancel={onClose}
            isSaving={update.isPending}
          />
        }
      />
    </SectionCard>
  );
}
